/**
 * Layout component for protected application routes
 * Part of the FAANG-grade _app.tsx refactoring - Phase 4.2
 *
 * Used for routes that require full application context:
 * - /dashboard and sub-routes
 * - /services (except /services/new for testing)
 * - /quotes, /customers, etc.
 * - All other authenticated routes
 */

import React from 'react';

import { AddressProvider } from '../../contexts/AddressContext';
import { MarketplaceSearchProvider } from '../../contexts/MarketplaceSearchContext';
import { TenantProvider } from '../../contexts/TenantContext';
import type { ProviderProps } from '../../types/app';
import Layout from '../layout/Layout';
import { RouteGuard } from '../layout/providers/RouteGuard';
import PrecacheRoutes from '../PrecacheRoutes';

/**
 * Conditional providers that depend on user role
 *
 * NOTE: CartProvider is NOT here — it lives in AppProviders as the single
 * source of truth. Having it in both AppProviders AND here caused duplicate
 * mounting, double API calls, double event listeners, and 401 errors during
 * logout. The CartProvider already guards itself with role/token checks.
 */
const ConditionalProviders: React.FC<ProviderProps> = ({ children }) => {
  return (
    <AddressProvider>
      <TenantProvider>
        <MarketplaceSearchProvider>{children}</MarketplaceSearchProvider>
      </TenantProvider>
    </AddressProvider>
  );
};

/**
 * Full application layout for protected routes
 * Includes tenant context, marketplace search, route guards, and main layout
 */
export const AppLayout: React.FC<ProviderProps> = ({ children }) => {
  return (
    <ConditionalProviders>
      <RouteGuard>
        <Layout>
          <PrecacheRoutes />
          {children}
        </Layout>
      </RouteGuard>
    </ConditionalProviders>
  );
};
